import React, { useEffect, useState } from 'react';
import { BsPencilSquare } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import { Box, Grid, GridItem, Icon, IconButton, Input, Text } from '@chakra-ui/react';

import { getUserLevelLabel } from '../../constants/userLevels';
import { showModal } from '../../redux/modules/modal';
import { useSearchUsers } from '../../services/user-service';
import colors from '../../styles/theme/foundation/colors';

import EditUserModal from './EditUserModal';

const MIN_SEARCH_LENGTH = 2;

const useDebouncedValue = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const PinsAdminUsersTable = () => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);

  const { data, isLoading, isFetching } = useSearchUsers({ name: debouncedSearch, email: debouncedSearch });

  const userList = data?.data ?? [];

  const showResults = debouncedSearch.trim().length >= MIN_SEARCH_LENGTH;

  const handleEditUser = user => {
    dispatch(showModal(
      () => (<EditUserModal user={user} />),
      {
        title: 'Edit User Details',
        size: '4xl'
      }
    ));
  };

  return (
    <Box bg={colors.white} maxH="700px" overflowY="auto" p={6} w="1050px">
      <Text textStyle="displayXs">PINS Admin</Text>

      <Text textStyle="bodyLg">Search all PINS users</Text>

      <Box justifyContent="flex-end" textAlign="right">
        <Input
          placeholder="Search by name or email"
          w="300px"
          onChange={e => setSearch(e.target.value)}
        />
      </Box>

      {!showResults && (
        <Text mt={4}>
          {`Type at least ${MIN_SEARCH_LENGTH} characters to search.`}
        </Text>
      )}

      {showResults && (isLoading || isFetching) && <Text mt={4}>Loading...</Text>}

      {showResults && !isLoading && !isFetching && !userList.length && <Text mt={4}>No users found.</Text>}

      {showResults && !isLoading && (
        <Box p={4} textAlign="left">
          <Grid
            gap={6}
            gridTemplateColumns="repeat(12, 1fr);"
          >
            <GridItem alignSelf="center" colSpan={3} justifySelf="left" ml={2} textStyle="bodyLg500">Name</GridItem>
            <GridItem alignSelf="center" colSpan={3} justifySelf="left" ml={2} textStyle="bodyLg500">Email</GridItem>
            <GridItem alignSelf="center" colSpan={2} justifySelf="left" ml={2} textStyle="bodyLg500">Phone</GridItem>
            <GridItem alignSelf="center" colSpan={2} justifySelf="left" ml={2} textStyle="bodyLg500">User Level</GridItem>
            <GridItem alignSelf="center" colSpan={2} justifySelf="left" ml={2} textStyle="bodyLg500">Edit User</GridItem>
          </Grid>

          {userList.map(u => (
            <Grid
              gap={6}
              gridTemplateColumns="repeat(12, 1fr);"
              key={u.userId}
              my={4}
            >
              <GridItem alignSelf="center" colSpan={3} justifySelf="left" ml={2}>
                {`${u.userFirstName ?? ''} ${u.userLastName ?? ''}`}
              </GridItem>
              <GridItem alignSelf="center" colSpan={3} justifySelf="left" ml={2}>
                {u.userEmail}
              </GridItem>
              <GridItem alignSelf="center" colSpan={2} justifySelf="left" ml={2}>
                {u.userPhone}
              </GridItem>
              <GridItem alignSelf="center" colSpan={2} justifySelf="left" ml={2}>
                {getUserLevelLabel(u.userLevel)}
              </GridItem>
              <GridItem alignSelf="center" colSpan={2} justifySelf="left" ml={2}>
                <IconButton
                  aria-label="edit-user-icon"
                  icon={<Icon as={BsPencilSquare} boxSize="18px" />}
                  size="xs"
                  variant="textGrey"
                  onClick={() => handleEditUser(u)}
                />
              </GridItem>
            </Grid>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default PinsAdminUsersTable;
